import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DataLocation } from "@shared/types/dataLocation";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";

/**
 * Your data (ADR 0011): `GET /api/v2/instances/current/data-location`, where skyData puts this
 * instance's data. Built from the binding and skyData's own records, so it changes only when the
 * destination or the connections change: read once, a 5-minute stale time and no poll. The key
 * carries the instance id, so one instance's address never shows under another's header.
 */
export const DATA_LOCATION_PATH = "/api/v2/instances/current/data-location";

export function dataLocationQueryKey(instanceId: string | null): [string, string | null] {
  return [DATA_LOCATION_PATH, instanceId];
}

export function useDataLocation(): UseQueryResult<DataLocation> {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id ?? null;
  return useQuery<DataLocation>({
    queryKey: dataLocationQueryKey(instanceId),
    enabled: instanceId !== null,
    queryFn: ({ signal }) => apiRequest<DataLocation>(DATA_LOCATION_PATH, { signal }),
    staleTime: 5 * 60_000,
  });
}
